/**
 * Market-hours + data-freshness helpers for the Accounts hero indicator.
 *
 * Scope (Apr 2026):
 *  • US equities regular trading hours only — 9:30–16:00 America/New_York,
 *    Monday–Friday. Holidays are ignored (NYSE publishes the calendar, but
 *    wiring it up is future work — the hero indicator is advisory, not
 *    authoritative, so a mis-rendered green dot on Thanksgiving won't
 *    break anything).
 *  • Pre-market (04:00–09:30) and after-hours (16:00–20:00) are treated
 *    as "closed" for the dot. A separate
 *    "extended-hours" state can ship later if users need it.
 *
 * The freshness rule (per product decision):
 *  • During RTH, data is "fresh" if it's < FRESHNESS_WINDOW_MS old.
 *  • Outside RTH, data is "fresh" if the update happened at/after the
 *    most-recent 16:00 NY close that has already elapsed. In practice
 *    that means a Friday 16:00 close stays fresh through Saturday,
 *    Sunday, and Monday pre-market — only flipping to stale at 09:30 ET
 *    Monday when the market re-opens. Likewise a Tuesday 16:00 close
 *    stays fresh through Wednesday pre-market. Otherwise stale.
 *
 * We use `Intl.DateTimeFormat` with `timeZone: "America/New_York"` to
 * sidestep bundling a full IANA tz library. It's accurate for wall-clock
 * readout and it handles DST transitions correctly because the platform
 * does the heavy lifting.
 */

import { isTradingDay } from "./trading-calendar";

export type MarketState = "open" | "closed";

export type FreshnessState = "fresh" | "stale";

/**
 * NYSE date in `YYYY-MM-DD` form, derived from `now` projected onto the
 * America/New_York wall clock. Used for `isTradingDay()` lookups so the
 * holiday calendar is consulted with the correct calendar date even when
 * the runner's local timezone is hours ahead/behind ET.
 */
function nyDateIso(p: NyParts): string {
  const mm = String(p.month).padStart(2, "0");
  const dd = String(p.day).padStart(2, "0");
  return `${p.year}-${mm}-${dd}`;
}

/** During RTH: any update younger than this counts as fresh. */
export const FRESHNESS_WINDOW_MS = 5 * 60 * 1000;

interface NyParts {
  year: number;
  month: number; // 1-12
  day: number;   // 1-31
  weekday: number; // 0 = Sun, 1 = Mon, …, 6 = Sat
  hour: number;  // 0-23
  minute: number; // 0-59
}

/**
 * Break a `Date` down into its America/New_York wall-clock parts. Using
 * `Intl.DateTimeFormat` with named parts is the cleanest way to get
 * timezone-correct numbers without pulling in date-fns-tz or luxon.
 */
function getNyParts(now: Date): NyParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
  const parts = fmt.formatToParts(now);
  const pick = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";

  // Map 'Sun'..'Sat' → 0..6 so the weekday maths matches `Date#getDay`.
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6
  };

  // `hour` comes back as "24" on the stroke of midnight in some locales.
  // Coerce via `% 24` for safety — we only use it for open/close checks,
  // not for cross-day arithmetic.
  const rawHour = Number(pick("hour"));
  const hour = Number.isFinite(rawHour) ? rawHour % 24 : 0;

  return {
    year: Number(pick("year")),
    month: Number(pick("month")),
    day: Number(pick("day")),
    weekday: weekdayMap[pick("weekday")] ?? 0,
    hour,
    minute: Number(pick("minute"))
  };
}

/**
 * Express a `Date` as a "NY wall-clock ms" value built from its
 * America/New_York parts. Two Dates compared this way are compared on
 * the NY wall clock plane, which sidesteps DST offset arithmetic: if
 * both `a` and `b` are expressed in NY wall clock, `a >= b` means "a
 * is at the same or later NY-local instant than b", regardless of the
 * UTC offset at either moment.
 *
 * We purposely build with `Date.UTC(...)` — the returned value is NOT
 * a real UTC timestamp; it's a synthetic "NY-wall-clock-as-if-UTC" ms
 * suitable for ordering comparisons only.
 */
function nyWallClockMs(d: Date): number {
  const p = getNyParts(d);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
}

/**
 * Most-recent weekday 16:00 NY close that has already elapsed as of
 * `now`, returned as a synthetic NY-wall-clock ms (see `nyWallClockMs`).
 *
 * Logic (only called when market is closed, so `now` is either a
 * weekend or a weekday outside 09:30–16:00 ET):
 *  • Weekday after 16:00 ET → today 16:00.
 *  • Weekday before 09:30 ET → previous weekday 16:00. Monday rolls
 *    back to Friday; Tue–Fri to the previous day.
 *  • Weekend → Friday 16:00.
 */
function mostRecentCloseNyMs(now: Date): number {
  const p = getNyParts(now);
  const todayCloseMs = Date.UTC(p.year, p.month - 1, p.day, 16, 0);
  const nowWallMs = nyWallClockMs(now);

  // Start at today's 16:00 NY; if we haven't reached it yet, step back
  // one day. Then walk backward over weekends to land on a weekday.
  let closeMs = nowWallMs >= todayCloseMs
    ? todayCloseMs
    : todayCloseMs - 24 * 60 * 60 * 1000;

  // Since `closeMs` was built via `Date.UTC` (synthetic NY wall clock),
  // read its weekday with `getUTCDay` so we don't accidentally apply the
  // runner's local tz offset to the roll-back. Walk back over weekends
  // AND NYSE holidays — a Friday-after-Thanksgiving morning should
  // consider Wednesday's close as the most-recent close, not
  // Thanksgiving's (which never happened).
  const isClosedDay = (ms: number): boolean => {
    const d = new Date(ms);
    const wd = d.getUTCDay();
    if (wd === 0 || wd === 6) return true;
    const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    return !isTradingDay(iso);
  };
  while (isClosedDay(closeMs)) {
    closeMs -= 24 * 60 * 60 * 1000;
  }
  return closeMs;
}

/**
 * Returns `"open"` when `now` falls inside NYSE regular trading hours on a
 * trading day, `"closed"` otherwise. "Trading day" = weekday AND not on
 * the NYSE holiday list (see `src/lib/trading-calendar.ts`).
 *
 * Holiday-awareness was added 2026-04-25 because this function gates more
 * than just the advisory hero dot — it controls the T1 refresh scheduler,
 * focus-regained refresh, and (newly) the idle-confirm modal. Polling
 * Alpaca on Thanksgiving is a waste of subrequest budget; nagging the
 * user to refresh on a day no new data is publishing is worse UX.
 */
export function getMarketState(now: Date = new Date()): MarketState {
  const parts = getNyParts(now);
  // 0 = Sun, 6 = Sat — weekends are always closed.
  if (parts.weekday === 0 || parts.weekday === 6) return "closed";
  // NYSE holiday — also closed (full closures only; partial-day early
  // closes like the day after Thanksgiving are not modelled — those
  // currently surface as "open" until 16:00 ET, which is acceptable
  // since the only behavioural cost is a slightly-late dot flip).
  if (!isTradingDay(nyDateIso(parts))) return "closed";

  const minutesSinceMidnight = parts.hour * 60 + parts.minute;
  const open = 9 * 60 + 30;
  const close = 16 * 60;
  return minutesSinceMidnight >= open && minutesSinceMidnight < close
    ? "open"
    : "closed";
}

/**
 * Decide fresh/stale for a given last-update timestamp.
 *
 *  • RTH + updated in the last 5 min → fresh.
 *  • Outside RTH + the update happened at/after the most-recent 16:00
 *    NY close → fresh. Friday 16:00 stays fresh through Sat/Sun and
 *    Monday pre-market, flipping to stale at 09:30 ET Monday when the
 *    next session opens (and the RTH rule above takes over).
 *  • Otherwise → stale.
 */
export function getFreshnessState(
  lastUpdate: Date,
  now: Date = new Date()
): FreshnessState {
  const market = getMarketState(now);
  if (market === "open") {
    return now.getTime() - lastUpdate.getTime() < FRESHNESS_WINDOW_MS
      ? "fresh"
      : "stale";
  }

  // Market closed — compare on the NY wall clock. `lastUpdate` is fresh
  // if it happened at or after the most-recent elapsed 16:00 NY close.
  const closeMs = mostRecentCloseNyMs(now);
  const updateMs = nyWallClockMs(lastUpdate);
  return updateMs >= closeMs ? "fresh" : "stale";
}

/**
 * Human-readable "X ago" — used inside the freshness tooltip. Short
 * form on purpose ("just now", "3m ago", "2h ago", "yesterday").
 */
export function formatAgo(lastUpdate: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - lastUpdate.getTime();
  if (diffMs < 0) return "just now";

  const s = Math.floor(diffMs / 1000);
  if (s < 45) return "just now";

  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;

  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;

  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}

/**
 * Deterministic mock last-update clock — we don't have a live quote
 * feed yet, so the hero dot needs something to chew on.
 *
 * Behaviour:
 *  • During RTH: returns a time between 30s and 8min ago. The 5min
 *    freshness threshold cuts right through that range so the dot
 *    flips green/yellow as the session rolls forward without a page
 *    refresh.
 *  • Outside RTH on a weekday: returns 16:00 NY time today (the
 *    "today's close" case), which `getFreshnessState` treats as fresh.
 *  • Weekends: returns Friday 16:00 NY time (last close). The updated
 *    freshness rule keeps the dot GREEN through the weekend because
 *    Friday's close is still the most-recent elapsed 16:00 ET close
 *    until Monday 09:30 reopens the session. That matches the user's
 *    expectation ("Sunday shows updated from Friday close → dot stays
 *    green, only flips yellow once Monday 09:31 rolls around").
 *
 * Deterministic per-minute — calling this twice in the same minute
 * returns the same timestamp so the dot doesn't flicker during rapid
 * re-renders. The minute granularity is below the freshness threshold
 * so UX still feels live.
 */
export function getMockLastUpdate(now: Date = new Date()): Date {
  const market = getMarketState(now);
  const ny = getNyParts(now);

  if (market === "open") {
    // Seed a 30s–8min "ago" value from the current wall-clock minute
    // so it's stable within a minute but varies across renders.
    const seedBase = ny.year * 10000 + ny.month * 100 + ny.day;
    const seed = seedBase * 1440 + ny.hour * 60 + ny.minute;
    // Simple LCG step for a non-terrible pseudo-random spread.
    const pseudo = (seed * 1664525 + 1013904223) >>> 0;
    const ageSec = 30 + (pseudo % 450); // 30s .. 8m
    return new Date(now.getTime() - ageSec * 1000);
  }

  // Market closed — roll back to the most recent 16:00 NY close. For
  // a weekday outside RTH, that's today 16:00; for weekends, Friday
  // 16:00. We approximate "today 16:00 NY" by using the NY parts and
  // constructing a Date in the browser's local tz, then adjusting —
  // but since this is demo-grade data, we accept a ~hour of TZ drift
  // on DST boundaries and just use `Date.UTC`-style construction.
  const today = new Date(now);
  today.setHours(16, 0, 0, 0);
  if (ny.weekday === 6) {
    // Saturday — roll back one day to Friday.
    today.setDate(today.getDate() - 1);
  } else if (ny.weekday === 0) {
    // Sunday — roll back two days to Friday.
    today.setDate(today.getDate() - 2);
  } else if (ny.hour < 16) {
    // Pre-market on a weekday — yesterday's close is the relevant one.
    // Step back to Friday if yesterday was Saturday (we're on Monday).
    today.setDate(today.getDate() - (ny.weekday === 1 ? 3 : 1));
  }
  // Weekday post-close stays at today 16:00 — no adjustment needed.
  return today;
}
